"use client"

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../components/layout/ThemeProvider';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { getProjectsByCategory } from '@/data/portfolio-projects';

const PortfolioPage = () => {
  const { theme } = useTheme();
  const searchParams = useSearchParams();
  const initialCategory = searchParams ? searchParams.get('categoria') || 'todos' : 'todos';
  const [categoriaAtiva, setCategoriaAtiva] = useState(initialCategory);

  // Atualizar a URL quando a categoria mudar
  useEffect(() => {
    const url = new URL(window.location.href);
    if (categoriaAtiva === 'todos') {
      url.searchParams.delete('categoria');
    } else {
      url.searchParams.set('categoria', categoriaAtiva);
    }
    window.history.replaceState({}, '', url.toString());
  }, [categoriaAtiva]);

  // Atualizar o estado quando os parâmetros da URL mudarem
  useEffect(() => {
    const categoria = searchParams?.get('categoria') || 'todos';
    setCategoriaAtiva(categoria);
  }, [searchParams]);

  // Configurações de animação
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Lista de categorias para filtragem
  const categorias = [
    { id: 'todos', nome: 'Todos' },
    { id: 'desenvolvimento-web', nome: 'Desenvolvimento Web' },
    { id: 'desenvolvimento-sistemas', nome: 'Desenvolvimento de Sistemas' },
    { id: 'manutencao', nome: 'Manutenção' },
    { id: 'microsolda', nome: 'Microsolda' },
    { id: 'infraestrutura-rede', nome: 'Infraestrutura' },
    { id: 'automacao-residencial', nome: 'Automação' },
    { id: 'energia-solar', nome: 'Energia Solar' }
  ];

  // Filtragem de projetos por categoria
  const projetosFiltrados = getProjectsByCategory(categoriaAtiva);

  // Formatar data para exibição
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Define as cores baseadas no tema
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-700';
  const headingColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const bgColor = theme === 'dark' ? 'bg-gray-900' : 'bg-white';
  const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className={`min-h-screen pt-16 ${bgColor}`}>
      {/* Botão para alternar tema */}
      <ThemeToggle showText={true} />
      
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Portfólio
                </span>
              </li>
              {categoriaAtiva !== 'todos' && (
                <>
                  <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </li>
                  <li>
                    <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                      {categorias.find(cat => cat.id === categoriaAtiva)?.nome || ''}
                    </span>
                  </li>
                </>
              )}
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero section */}
      <section className={`relative py-12 overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'}`} aria-labelledby="portfolio-heading">
        {/* Background elements */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h1 
            id="portfolio-heading" 
            className={`text-3xl md:text-4xl font-bold mb-3 text-center ${headingColor}`}
          >
            Nosso <span className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>Portfólio</span>
          </h1>
          
          <p className={`text-lg text-center max-w-3xl mx-auto ${textColor}`}>
            Projetos de sucesso desenvolvidos pela Web Imports para nossos clientes.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        {/* Filtro de categorias */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categorias.map((categoria) => (
            <button
              key={categoria.id}
              onClick={() => setCategoriaAtiva(categoria.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                categoriaAtiva === categoria.id
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {categoria.nome}
            </button>
          ))}
        </div>

        {/* Grade de projetos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projetosFiltrados.map((projeto) => (
            <motion.div
              key={projeto.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className={`rounded-xl overflow-hidden border ${borderColor} transition-all hover:shadow-lg`}
            >
              {/* Comparação antes/depois */}
              <div className="relative h-48 w-full overflow-hidden">
                <div className="absolute inset-0 flex">
                  <div className="w-1/2 relative">
                    <div className="absolute inset-0 bg-gray-900/20 z-10"></div>
                    <Image
                      src={projeto.beforeImage}
                      alt={`${projeto.title} - Antes`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-20">Antes</div>
                  </div>
                  <div className="w-1/2 relative">
                    <div className="absolute inset-0 bg-gray-900/20 z-10"></div>
                    <Image
                      src={projeto.afterImage}
                      alt={`${projeto.title} - Depois`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-20">Depois</div>
                  </div>
                </div>
              </div>
              
              <div className={`p-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className={`text-xl font-bold ${headingColor}`}>{projeto.title}</h3>
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatDate(projeto.date)}
                  </span>
                </div>
                
                <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Cliente: {projeto.clientName}
                </p>
                
                <p className={`mb-4 text-sm ${textColor}`}>
                  {projeto.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {projeto.tags.map((tag) => (
                    <span 
                      key={tag} 
                      className={`text-xs px-2 py-1 rounded-full ${
                        theme === 'dark' 
                          ? 'bg-gray-700 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <button 
                  className={`w-full py-2 rounded text-center transition-colors ${
                    theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  Ver Detalhes
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        
        {projetosFiltrados.length === 0 && (
          <div className={`text-center py-12 ${textColor}`}>
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <h3 className="text-xl font-bold mb-2">Nenhum projeto encontrado</h3>
            <p className="max-w-md mx-auto">
              Não encontramos projetos nesta categoria no momento. Por favor, selecione outra categoria ou volte em breve.
            </p>
          </div>
        )}
        
        {/* Call to Action */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className={`text-center py-12 px-6 rounded-lg mt-12 ${theme === 'dark' ? 'bg-gradient-to-br from-blue-900 to-blue-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}
        >
          <h2 className={`text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Vamos Transformar seu Próximo Projeto em Realidade?
          </h2>
          <p className={`text-base mb-5 max-w-2xl mx-auto ${theme === 'dark' ? 'text-white' : 'text-gray-700'}`}>
            Conte com nossa experiência e expertise para desenvolver soluções tecnológicas de alto nível para o seu negócio.
          </p>
          <motion.a 
            href="/contato" 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-300 inline-block shadow-lg hover:shadow-xl"
          >
            Fale com Nossa Equipe
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

export default PortfolioPage; 